import { useCallback, useEffect, useRef } from "react";
import {
    MapPointProps,
    MapRouteProps,
} from "../../components/framework/map/map";
import { useDebouncedEventListener } from "../../components/hooks/useDebouncedEventListener";
import { useMap } from "@vis.gl/react-google-maps";

const fitMapDelay = 300;

// Handles fitting map to bounds
export const useFitMap = (
    routes: MapRouteProps[],
    points: MapPointProps[],
    autoZoom: boolean,
    defaultMinZoom : number = 12 // This controls how detailed autofit goes
): [boolean, () => boolean] => {
    const hasZoomed = useRef(false);
    const map = useMap();

    const fitMap = useCallback(() => {
        if (!map) return false;

        let bounds: google.maps.LatLngBounds;

        if (routes?.filter((r) => r.coords?.length > 0).length > 0) {
            // Calculate the bounds of all polylines
            bounds = routes.reduce(
                (acc, r) => r.coords?.reduce((a, c) => a.extend(c), acc),
                new google.maps.LatLngBounds()
            );
        }

        if (points?.filter((r) => r.coords).length > 0) {
            // Get the bounds from all points
            bounds = points.reduce(
                (acc, r) => (r.coords ? acc.extend(r.coords) : acc),
                bounds || new google.maps.LatLngBounds()
            );
        }

        if (bounds) {
            // Fit the map to the calculated bounds
            map.fitBounds(bounds);
            // Listen zoom after fitBounds and limit initial zoom to certain level. 
            // This makes sure that zoom is not too precize if only one or few points are shown
            google.maps.event.addListenerOnce(map, "idle", () => {
                const zoom = map.getZoom();
                if (zoom && zoom > defaultMinZoom) {
                    map.setZoom(defaultMinZoom);
                }
            });
        }

        return true;
    }, [map, routes, points]);

    useEffect(() => {
        // Zoom on inital render when autozoom is off
        if (autoZoom || !hasZoomed.current) {
            hasZoomed.current = fitMap();
        }
    }, [fitMap, autoZoom]);

    useDebouncedEventListener("resize", fitMap, fitMapDelay);

    return [hasZoomed.current, fitMap];
};
